import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';

import {
  KeyValueDefaultModel,
  KeyValueElevatedRoleModel,
  KeyValueGroupTransitions,
  KeyValueRelationModel,
  KeyValueType,
  KeyValueTypeModel,
} from '@models/common';
import { ScreenTypeEnum } from '@module-definition/screen/common';
import { TemplateTypeEnum } from '@shared/consts/template-type.enum';
import { HttpService } from '@shared/services';

import { FieldDataSource, ModuleFieldsFilter } from './module-config.model';

const OPEN_STATE_TYPE_ID = 2;

/**
 * Dictionaries the module configuration tabs need and `DictionaryStore` does not carry, because they
 * are scoped to a module and a workflow rather than to the tenant.
 *
 * The cache lives for one tab visit: the dialog clears it whenever it mounts a tab. Within a visit, the
 * writes clear the prefixes they make stale.
 */
@Injectable({ providedIn: 'root' })
export class ModuleConfigDictionariesService {
  private readonly http = inject(HttpService);
  private readonly cache = new Map<string, Observable<unknown>>();

  private cached<T>(key: string, load: () => Observable<T>): Observable<T> {
    if (!this.cache.has(key)) {
      this.cache.set(key, load().pipe(shareReplay({ bufferSize: 1, refCount: false })));
    }
    return this.cache.get(key) as Observable<T>;
  }

  private moduleHeaders(moduleId: number) {
    return { moduleId: moduleId.toString() };
  }

  /** Drops the cached dictionaries under a key prefix, or all of them without one. */
  invalidate(keyPrefix?: string): void {
    if (!keyPrefix) {
      this.cache.clear();
      return;
    }
    for (const key of Array.from(this.cache.keys())) {
      if (key === keyPrefix || key.startsWith(`${keyPrefix}|`)) {
        this.cache.delete(key);
      }
    }
  }

  getSignatureRoles(moduleTypeId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`signatureRoles|${moduleTypeId}`, () =>
      this.http
        .get<
          KeyValueElevatedRoleModel<number, string>[]
        >('_api/Role/Dict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ moduleTypeId }) } })
        .pipe(map(roles => (roles ?? []).filter(role => role.isUsed).map(({ key, value }) => ({ key, value })))),
    );
  }

  getClosedStates(moduleId: number, workflowId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`closedStates|${moduleId}|${workflowId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/StateDefinition/ClosedStatesDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ workflowId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(states => states ?? [])),
    );
  }

  getRoleUsers(roleId: number, moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.http
      .get<
        KeyValueType<number, string>[]
      >('_api/Role/RoleUsersDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ id: roleId }), headers: this.moduleHeaders(moduleId) } })
      .pipe(map(users => users ?? []));
  }

  getScreens(moduleId: number, type: ScreenTypeEnum): Observable<KeyValueType<number, string>[]> {
    return this.cached(`screens|${moduleId}|${type}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/Screen/Dict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ type }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(screens => screens ?? [])),
    );
  }

  getStateTypes(): Observable<KeyValueTypeModel<number>[]> {
    return this.cached('stateTypes', () =>
      this.http
        .get<KeyValueTypeModel<number>[]>('_api/StateDefinition/TypeDict', { useLoader: false })
        .pipe(map(types => types ?? [])),
    );
  }

  getConditionalFields(moduleId: number, screenId?: number): Observable<KeyValueTypeModel<number>[]> {
    const key = screenId ? `conditionalFields|${moduleId}|${screenId}` : `conditionalFields|${moduleId}`;
    const params = this.http.buildHttpParams(screenId ? { screenId, onlyCurrentScreen: true } : {});
    return this.cached(key, () =>
      this.http
        .get<
          KeyValueTypeModel<number>[]
        >('_api/TransitionDefinition/ConditionalsFieldsDict', { useLoader: false, httpOptions: { params, headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getOperators(): Observable<KeyValueTypeModel<number>[]> {
    return this.cached('operators', () =>
      this.http
        .get<KeyValueTypeModel<number>[]>('_api/TransitionDefinition/OperatorsDict', { useLoader: false })
        .pipe(map(operators => operators ?? [])),
    );
  }

  getInitiatorRoles(moduleId: number, workflowId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`initiators|${moduleId}|${workflowId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/StateDefinition/InitiatorRolesDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ workflowId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(roles => roles ?? [])),
    );
  }

  getStates(
    moduleId: number,
    workflowId: number,
    addDraft = false,
  ): Observable<KeyValueDefaultModel<number, string>[]> {
    return this.cached(`states|${moduleId}|${workflowId}|${addDraft}`, () =>
      this.http
        .get<
          KeyValueDefaultModel<number, string>[]
        >('_api/StateDefinition/Dict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ workflowId, addDraft }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(states => states ?? [])),
    );
  }

  getNotificationTemplates(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`notifications|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/TemplateDefinition/TemplatesDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ typeName: TemplateTypeEnum.Notification }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(templates => templates ?? [])),
    );
  }

  getModuleRelations(moduleId: number): Observable<KeyValueRelationModel<number, string>[]> {
    return this.cached(`relations|${moduleId}`, () =>
      this.http
        .get<
          KeyValueRelationModel<number, string>[]
        >('_api/ModuleRelation/Dict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(relations => relations ?? [])),
    );
  }

  getContractTypes(): Observable<KeyValueType<number, string>[]> {
    return this.cached('contractTypes', () =>
      this.http
        .get<KeyValueType<number, string>[]>('_api/EntityTemplates/ContractTypesDict', { useLoader: false })
        .pipe(map(types => types ?? [])),
    );
  }

  getRelationTypes(): Observable<KeyValueType<number, string>[]> {
    return this.cached('relationTypes', () =>
      this.http
        .get<KeyValueType<number, string>[]>('_api/ModuleRelation/RelationTypesDict', { useLoader: false })
        .pipe(map(types => types ?? [])),
    );
  }

  /** The modules a relation can point to. */
  getRelationModules(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`relationModules|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/ModuleRelation/DataSourceRelationDict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(modules => modules ?? [])),
    );
  }

  /** The types of any module, the one in the header: a relation needs the types of both its ends. */
  getModuleTypes(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`moduleTypes|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/ModuleType/TypesDict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(types => types ?? [])),
    );
  }

  getPreferredFields(moduleTypeId: number, id = 0): Observable<KeyValueType<number, string>[]> {
    return this.cached(`preferredFields|${moduleTypeId}|${id}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/Action/PreferredFields', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ moduleTypeId, id }) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getStateSignatures(moduleId: number, workflowId: number): Observable<KeyValueGroupTransitions<number>[]> {
    return this.cached(`stateSignatures|${moduleId}|${workflowId}`, () =>
      this.http
        .get<
          KeyValueGroupTransitions<number>[]
        >('_api/TransitionDefinition/SignList', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ workflowId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(signatures => signatures ?? [])),
    );
  }

  getSignatureTransitions(moduleId: number, workflowId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`signatureTransitions|${moduleId}|${workflowId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/ModuleGroupTransitionSign/SignList', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ workflowId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(transitions => transitions ?? [])),
    );
  }

  getModuleEntities(moduleId: number, moduleTypeId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`moduleEntities|${moduleId}|${moduleTypeId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/FieldDefinition/EntitiesDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ moduleTypeId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(entities => entities ?? [])),
    );
  }

  getDataSources(withParent: boolean): Observable<FieldDataSource[]> {
    return this.cached(`dataSources|${withParent}`, () =>
      this.http
        .get<
          FieldDataSource[]
        >('_api/ListsDefinition/DataSourceDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ withParent }) } })
        .pipe(map(sources => sources ?? [])),
    );
  }

  getAggregateFunctions(): Observable<KeyValueType<number, string>[]> {
    return this.cached('aggregateFunctions', () =>
      this.http
        .get<KeyValueType<number, string>[]>('_api/FieldDefinition/AggregateFunctionsDict', { useLoader: false })
        .pipe(map(functions => functions ?? [])),
    );
  }

  getModuleFields(moduleId: number, filter: ModuleFieldsFilter): Observable<KeyValueType<number, string>[]> {
    const { type, displayType, dataSourceId, multiple, addContractFields, includeSystemFields, includeSpecialFields } =
      filter;
    const key = [
      type,
      displayType,
      dataSourceId,
      multiple,
      addContractFields,
      includeSystemFields,
      includeSpecialFields,
    ];
    return this.cached(`moduleFields|${moduleId}|${key.join('|')}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/FieldDefinition/Dict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams(filter), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getEntityFields(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`entityFields|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/FieldDefinition/EntityFieldsDict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getDestinationFolderFields(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`destinationFolderFields|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/FieldDefinition/DestinationFolderFieldsDict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getOpenStates(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`openStates|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/StateDefinition/Dict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ typeId: OPEN_STATE_TYPE_ID }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(states => states ?? [])),
    );
  }

  getEmailFields(moduleId: number): Observable<KeyValueTypeModel<number>[]> {
    return this.cached(`emailFields|${moduleId}`, () =>
      this.http
        .get<
          KeyValueTypeModel<number>[]
        >('_api/LinkCorrespondenceToModule/Dict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getEmailLinkFields(moduleId: number, screenId: number): Observable<KeyValueTypeModel<number>[]> {
    return this.cached(`emailLinkFields|${moduleId}|${screenId}`, () =>
      this.http
        .get<
          KeyValueTypeModel<number>[]
        >('_api/FieldDefinition/LinkCorrespondenceFieldsDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ screenId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getGanttLabelFields(moduleId: number): Observable<KeyValueType<number, string>[]> {
    return this.cached(`ganttLabelFields|${moduleId}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/FieldDefinition/GanttViewLabelFieldsDict', { useLoader: false, httpOptions: { headers: this.moduleHeaders(moduleId) } })
        .pipe(map(fields => fields ?? [])),
    );
  }

  getHeadersFooters(moduleId: number, isHeader: boolean): Observable<KeyValueType<number, string>[]> {
    return this.cached(`headersFooters|${moduleId}|${isHeader}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/HeaderFooterSettings/Dict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ isHeader }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(items => items ?? [])),
    );
  }

  getPrintoutTemplates(
    moduleId: number,
    screenId: number,
    typeName: TemplateTypeEnum,
  ): Observable<KeyValueType<number, string>[]> {
    return this.cached(`printoutTemplates|${moduleId}|${screenId}|${typeName}`, () =>
      this.http
        .get<
          KeyValueType<number, string>[]
        >('_api/TemplateDefinition/TemplatesDict', { useLoader: false, httpOptions: { params: this.http.buildHttpParams({ typeName, withConditionals: false, screenId }), headers: this.moduleHeaders(moduleId) } })
        .pipe(map(templates => templates ?? [])),
    );
  }
}
